import { useState, useEffect } from "react";
import Modal from "./ui/Modal";
import { CATEGORIES, MARKETS } from "../data/mockData";
import { X, Upload, Image as ImageIcon } from "lucide-react";

const EMPTY = {
  name: "",
  category: "Electronics",
  price: "",
  stock: "",
  status: "active",
  featured: false,
  description: "",
  markets: [],
  images: [],
};

export default function ProductModal({
  isOpen,
  onClose,
  product,
  onSave,
  canFeature,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(product ? { ...product } : { ...EMPTY });
      setErrors({});
    }
  }, [isOpen, product]);

  const change = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field])
      setErrors((prev) => {
        const e = { ...prev };
        delete e[field];
        return e;
      });
  };

  const toggleMarket = (market) => {
    setForm((prev) => ({
      ...prev,
      markets: prev.markets.includes(market)
        ? prev.markets.filter((m) => m !== market)
        : [...prev.markets, market],
    }));
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files) return;

    for (let i = 0; i < Math.min(files.length, 5 - form.images.length); i++) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setForm((prev) => ({
          ...prev,
          images: [...prev.images, event.target.result],
        }));
      };
      reader.readAsDataURL(files[i]);
    }
  };

  const removeImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Product name is required.";
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0)
      e.price = "Enter a valid price.";
    if (
      form.stock === "" ||
      isNaN(Number(form.stock)) ||
      Number(form.stock) < 0
    )
      e.stock = "Enter a valid stock quantity.";
    if (form.markets.length === 0) e.markets = "Select at least one market.";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSave({
      ...form,
      price: Number(form.price),
      stock: Number(form.stock),
      status: Number(form.stock) === 0 ? "out_of_stock" : form.status,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? "Edit Product" : "Add New Product"}
      size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Product Name *
          </label>
          <input
            className="input-field"
            placeholder="e.g. iPhone 15 Pro Max"
            value={form.name}
            onChange={(e) => change("name", e.target.value)}
          />
          {errors.name && (
            <p className="text-xs text-rose-400 mt-1">{errors.name}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Category *
          </label>
          <select
            className="input-field"
            value={form.category}
            onChange={(e) => change("category", e.target.value)}>
            {CATEGORIES.map((c) => (
              <option
                key={c.id}
                value={c.name}
                className="bg-white dark:bg-gray-800">
                {c.icon} {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Price + Stock */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Price (₦) *
            </label>
            <input
              className="input-field"
              type="number"
              placeholder="0"
              min="0"
              value={form.price}
              onChange={(e) => change("price", e.target.value)}
            />
            {errors.price && (
              <p className="text-xs text-rose-400 mt-1">{errors.price}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Stock *
            </label>
            <input
              className="input-field"
              type="number"
              placeholder="0"
              min="0"
              value={form.stock}
              onChange={(e) => change("stock", e.target.value)}
            />
            {errors.stock && (
              <p className="text-xs text-rose-400 mt-1">{errors.stock}</p>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Status
          </label>
          <select
            className="input-field"
            value={form.status}
            onChange={(e) => change("status", e.target.value)}>
            <option value="active" className="bg-white dark:bg-gray-800">
              Active
            </option>
            <option value="inactive" className="bg-white dark:bg-gray-800">
              Inactive
            </option>
            <option value="out_of_stock" className="bg-white dark:bg-gray-800">
              Out of Stock
            </option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Description
          </label>
          <textarea
            className="input-field resize-none"
            rows={3}
            placeholder="Describe the product..."
            value={form.description}
            onChange={(e) => change("description", e.target.value)}
          />
        </div>

        {/* Markets */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Select Markets *
            <span className="text-gray-500 dark:text-gray-600 ml-1">
              ({form.markets.length} selected)
            </span>
          </label>
          {errors.markets && (
            <p className="text-xs text-rose-400 mb-2">{errors.markets}</p>
          )}
          <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto bg-gray-100 dark:bg-gray-800/30 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
            {MARKETS.map((market) => (
              <label
                key={market}
                className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800/50 transition-colors">
                <input
                  type="checkbox"
                  checked={form.markets.includes(market)}
                  onChange={() => toggleMarket(market)}
                  className="w-4 h-4 rounded cursor-pointer accent-violet-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {market}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Images */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Product Images
            <span className="text-gray-500 dark:text-gray-600 ml-1">
              ({form.images.length}/5)
            </span>
          </label>
          <div className="space-y-3">
            {/* Upload area */}
            {form.images.length < 5 && (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors bg-gray-50 dark:bg-gray-800/30">
                <Upload
                  size={24}
                  className="text-gray-400 dark:text-gray-500 mb-2"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-600 mt-1">
                  PNG, JPG up to 5MB each
                </span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}

            {/* Image preview grid */}
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 aspect-square">
                    <img
                      src={img}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {idx === 0 && (
                      <div className="absolute top-1 left-1 bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        Cover
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={16} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {form.images.length === 0 && (
              <div className="text-center py-8 text-gray-600">
                <ImageIcon size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No images added yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Featured toggle */}
        <div className="flex items-center gap-3">
          <label
            className={`relative inline-flex items-center cursor-pointer ${!canFeature ? "opacity-50" : ""}`}>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={form.featured}
              disabled={!canFeature}
              onChange={(e) => change("featured", e.target.checked)}
            />
            <div className="w-10 h-5 bg-gray-300 dark:bg-gray-700 peer-checked:bg-violet-600 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-5" />
          </label>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Featured listing
            {!canFeature && (
              <span className="text-xs text-amber-500 ml-2">
                (Upgrade plan to enable)
              </span>
            )}
          </span>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-800">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {product ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
